import { queueAPI, timeLogsAPI } from "../../../api/client";
import useAuth from "../../../hooks/useAuth";
import TodayTaskList from "../../../components/schedule/TodayTaskList";
import WeekMiniCalendar from "../../../components/schedule/WeekMiniCalendar";
import {
  formatDisplayDate,
  parseLocalDate,
  toLocalDateString,
  todayString,
} from "../../../utils/dateUtils";
import "../../../components/projects/Projects.css";
import { useEffect, useMemo, useState } from "react";

function buildWeek() {
  const now = new Date();
  const todayStr = toLocalDateString(now);
  const start = new Date(now);
  start.setDate(now.getDate() - now.getDay() + 1);
  return Array.from({ length: 7 }).map((_, idx) => {
    const date = new Date(start);
    date.setDate(start.getDate() + idx);
    const dateStr = toLocalDateString(date);
    return {
      date: dateStr,
      label: date.toLocaleDateString(undefined, { weekday: "short" }),
      day: date.getDate(),
      isToday: dateStr === todayStr,
      dots: 0,
    };
  });
}

export default function MySchedulePage() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => todayString());
  const week = useMemo(() => buildWeek(), []);

  useEffect(() => {
    if (!user?.id) return;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [queueResp, activeResp] = await Promise.all([
          queueAPI.getUserQueue(user.id, {
            from: week[0].date,
            to: week[week.length - 1].date,
          }),
          timeLogsAPI.active(),
        ]);
        setQueue(queueResp.data || []);
        setActive(activeResp.data);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load schedule");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.id, week]);
  const tasksByDate = useMemo(() => {
    const map = new Map();

    queue.forEach((task) => {
      const startStr = toLocalDateString(task.startDate || task.assignedAt);
      const endStr = task.expectedEndDate
        ? toLocalDateString(task.expectedEndDate)
        : startStr; // if no end date, only show on start date

      if (!startStr) return;

      // Add task to every day from startStr to endStr inclusive
      let current = parseLocalDate(startStr);
      const end = parseLocalDate(endStr);

      while (current <= end) {
        const key = toLocalDateString(current);
        if (!map.has(key)) map.set(key, []);
        // Avoid duplicates if task already added for this day
        if (!map.get(key).find((t) => t.id === task.id)) {
          map.get(key).push(task);
        }
        current.setDate(current.getDate() + 1);
      }
    });

    return map;
  }, [queue]);
  const selectedTasks = tasksByDate.get(selectedDate) || [];
  const selectedDateObj = parseLocalDate(selectedDate) || new Date();

  return (
    <div className="project-detail-page">
      <div className="project-detail-header">
        <div>
          <h1>What you're working on</h1>
          <div className="small-muted">
            {formatDisplayDate(selectedDateObj, {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </div>
        </div>
      </div>

      {loading && <div className="small-muted">Loading...</div>}
      {error && (
        <div className="small-muted" style={{ color: "#b91c1c" }}>
          {error}
        </div>
      )}

      {!loading && (
        <div className="project-detail-grid">
          <div className="project-detail-aside">
            {active && (
              <div className="panel" style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600 }}>Currently working on</div>
                <div style={{ marginTop: 6 }}>
                  {active.stageAssignment?.stageName} ·{" "}
                  {active.stageAssignment?.project?.name}
                </div>
                <button
                  className="btn-secondary"
                  style={{ marginTop: 8 }}
                  onClick={async () => {
                    await timeLogsAPI.pause(active.stageAssignmentId);
                    setActive(null);
                  }}
                >
                  Take a Break
                </button>
              </div>
            )}
            <TodayTaskList
              tasks={selectedTasks}
              label={formatDisplayDate(selectedDateObj, {
                weekday: "long",
                day: "numeric",
                month: "long",
                // hour: "numeric",
                // minute: "2-digit",
              })}
              onStart={() => {}}
              onView={() => {}}
            />
          </div>

          <div className="project-tracker">
            <WeekMiniCalendar
              days={week}
              selectedDate={selectedDate}
              onSelect={(day) => setSelectedDate(day.date)}
            />
            <div className="panel" style={{ marginTop: 12 }}>
              <h4 style={{ marginTop: 0 }}>Tasks for selected day</h4>
              {selectedTasks.length === 0 && (
                <div className="small-muted">No tasks for this day.</div>
              )}
              {selectedTasks.map((task) => (
                <div key={task.id} className="small-muted">
                  {task.stageName} · {task.projectName}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
